import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLElement} [viewMorePrevious] - The previous-page sentinel.
 * @property {HTMLElement} [viewMoreNext] - The next-page sentinel.
 * @property {HTMLAnchorElement} [loadMoreNext] - The progressive next-page link.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #isLoadingNextPage = false;

  /** @type {boolean} */
  #isRestoringHistory = false;

  /** @type {string} */
  #activeRestoreId = '';

  /** @type {MediaQueryList} */
  #desktopLoadMoreMediaQuery = window.matchMedia('(min-width: 990px)');

  /** @type {number} */
  #cacheGeneration = 0;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.#restoreCollectionState();

    window.addEventListener('pageshow', this.#handlePageShow);

    if (this.hasAttribute('desktop-load-more')) {
      this.#desktopLoadMoreMediaQuery.addEventListener('change', this.#handleDisplayModeChange);
    }

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#desktopLoadMoreMediaQuery.removeEventListener('change', this.#handleDisplayModeChange);
    window.removeEventListener('pageshow', this.#handlePageShow);
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * @returns {boolean} Whether products must be loaded manually on desktop.
   */
  #isDesktopLoadMoreMode() {
    return this.hasAttribute('desktop-load-more') && this.#desktopLoadMoreMediaQuery.matches;
  }

  #handlePageShow = () => {
    this.#restoreCollectionState();
  };

  /**
   * Restores the pages that were progressively appended before visiting a product.
   * The history state is written by product-card.js for the OC collection on desktop and mobile.
   */
  async #restoreCollectionState() {
    const restoreState = this.#getCollectionRestoreState();

    if (!restoreState || this.#activeRestoreId === restoreState.id) return;

    const { grid, loadMoreNext } = this.refs;
    if (!grid) return;

    this.#activeRestoreId = restoreState.id;
    this.#isRestoringHistory = true;
    this.setAttribute('data-restoring-history', 'true');

    if (loadMoreNext instanceof HTMLAnchorElement) {
      loadMoreNext.setAttribute('aria-busy', 'true');
      loadMoreNext.setAttribute('aria-disabled', 'true');
    }

    const restoreGeneration = this.#cacheGeneration;

    try {
      const totalPages = Number(grid.dataset.lastPage);
      const lastPage = Math.min(
        restoreState.lastPage,
        Number.isInteger(totalPages) ? totalPages : restoreState.lastPage
      );
      const loadedPages = this.#getLoadedPageNumbers(grid);
      const missingPages = [];

      for (let page = 2; page <= lastPage; page += 1) {
        if (!loadedPages.has(page)) missingPages.push(page);
      }

      await Promise.all(
        missingPages.map((page) => this.#fetchSpecificPage(page, undefined, restoreGeneration))
      );

      if (!this.isConnected || restoreGeneration !== this.#cacheGeneration) return;

      for (const page of missingPages) {
        const pageItems = this.#getGridForPage(page);
        if (!pageItems) throw new Error(`Collection page ${page} could not be restored.`);

        grid.append(...Array.from(pageItems));
      }

      if (missingPages.length > 0) {
        this.#aspectRatioHelper?.processNewElements();
      }

      if (lastPage > 1) {
        this.#syncNextPageControls(lastPage);
      }

      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });

      this.#restoreScrollPosition(restoreState);

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } catch (error) {
      console.error('Unable to restore the previously loaded collection products.', error);

      const resultsList = document.getElementById('ResultsList');
      resultsList?.scrollIntoView({ behavior: 'instant', block: 'start' });
    } finally {
      this.#isRestoringHistory = false;
      this.removeAttribute('data-restoring-history');

      if (loadMoreNext instanceof HTMLAnchorElement && loadMoreNext.isConnected) {
        loadMoreNext.removeAttribute('aria-busy');
        loadMoreNext.removeAttribute('aria-disabled');
      }

      history.scrollRestoration = 'auto';
    }
  }

  /**
   * @returns {{id: string, url: string, lastPage: number, anchor: string, cardViewportTop: number, scrollY: number, snapshotKey: string} | undefined}
   */
  #getCollectionRestoreState() {
    if (!this.classList.contains('oc-collection-main') || this.getAttribute('infinite-scroll') !== 'true') return;

    const historyState = history.state;
    const restoreState =
      historyState && typeof historyState === 'object' ? historyState.ocCollectionRestore : undefined;

    if (!restoreState || restoreState.version !== 1) return;

    const lastPage = Number(restoreState.lastPage);
    if (!restoreState.id || !restoreState.url || !Number.isInteger(lastPage) || lastPage < 1) return;

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('page');
    currentUrl.hash = '';

    if (restoreState.url !== `${currentUrl.pathname}${currentUrl.search}`) return;

    return {
      id: String(restoreState.id),
      url: String(restoreState.url),
      lastPage,
      anchor: String(restoreState.anchor || ''),
      cardViewportTop: Number(restoreState.cardViewportTop),
      scrollY: Number(restoreState.scrollY),
      snapshotKey: String(restoreState.snapshotKey || ''),
    };
  }

  /**
   * @param {HTMLUListElement} grid
   * @returns {Set<number>}
   */
  #getLoadedPageNumbers(grid) {
    return new Set(
      Array.from(grid.querySelectorAll(':scope > [ref="cards[]"][data-page]'))
        .map((card) => Number(/** @type {HTMLElement} */ (card).dataset.page))
        .filter((page) => Number.isInteger(page) && page > 0)
    );
  }

  /**
   * @param {{anchor: string, cardViewportTop: number, scrollY: number}} restoreState
   */
  #restoreScrollPosition(restoreState) {
    const anchor = restoreState.anchor ? document.getElementById(restoreState.anchor) : null;
    const card = anchor?.closest('li') || anchor;

    if (card instanceof HTMLElement && Number.isFinite(restoreState.cardViewportTop)) {
      const cardDocumentTop = card.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: Math.max(0, cardDocumentTop - restoreState.cardViewportTop),
        behavior: 'instant',
      });
      return;
    }

    if (Number.isFinite(restoreState.scrollY)) {
      window.scrollTo({ top: Math.max(0, restoreState.scrollY), behavior: 'instant' });
    }
  }

  #handleDisplayModeChange = () => {
    if (!this.isConnected) {
      this.#desktopLoadMoreMediaQuery.removeEventListener('change', this.#handleDisplayModeChange);
      this.infinityScrollObserver?.disconnect();
      return;
    }

    this.#observeViewMore();
    this.#restoreCollectionState();
  };

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    const isDesktopLoadMoreMode = this.#isDesktopLoadMoreMode();

    this.infinityScrollObserver?.disconnect();

    // The desktop OC layout keeps automatic loading for previous pages only.
    // Its next page is intentionally loaded through the real progressive link.
    if (!viewMorePrevious && (!viewMoreNext || isDesktopLoadMoreMode)) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext && !this.#isDesktopLoadMoreMode()) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext && !isDesktopLoadMoreMode) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    if (!page || !this.#shouldUsePage(page)) {
      return false;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
      return this.pages.has(page.page);
    } catch (error) {
      console.error(`Unable to preload collection page ${page.page}.`, error);
      return false;
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   * @param {number} [cacheGeneration] - Cache generation active when the request starts
   */
  async #fetchSpecificPage(pageNumber, url = undefined, cacheGeneration = this.#cacheGeneration) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    // The section renderer adds its own query parameter, so give it a copy and
    // preserve the clean storefront URL for links and browser history.
    const requestUrl = new URL(pageInfo.url);
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, requestUrl);

    if (cacheGeneration !== this.#cacheGeneration) return;

    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid || this.#isLoadingNextPage || this.#isRestoringHistory) return [];

    this.#isLoadingNextPage = true;

    try {
      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return [];
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        try {
          await this.#fetchSpecificPage(nextPage.page, nextPage.url);
        } catch (error) {
          console.error(`Unable to load collection page ${nextPage.page}.`, error);
          return [];
        }

        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return [];
      }

      const newItems = Array.from(nextPageItemElements);
      grid.append(...newItems);

      this.#aspectRatioHelper?.processNewElements();

      if (!this.#isDesktopLoadMoreMode()) {
        history.pushState('', '', nextPage.url.toString());
      }

      this.#syncNextPageControls(nextPage.page);

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });

      return newItems;
    } finally {
      this.#isLoadingNextPage = false;
    }
  }

  /**
   * Progressively loads the next page while preserving the link as a no-JS fallback.
   * @param {Event} event
   */
  async loadMore(event) {
    if (!this.#isDesktopLoadMoreMode()) return;

    event.preventDefault();

    if (this.#isRestoringHistory) return;

    const loadMoreNext = this.refs.loadMoreNext;
    if (!(loadMoreNext instanceof HTMLAnchorElement) || loadMoreNext.hidden || this.#isLoadingNextPage) return;

    const fallbackUrl = loadMoreNext.href;
    const loadGeneration = this.#cacheGeneration;
    const isKeyboardActivation =
      event instanceof KeyboardEvent || (event instanceof MouseEvent && event.detail === 0);

    loadMoreNext.setAttribute('aria-busy', 'true');
    loadMoreNext.setAttribute('aria-disabled', 'true');

    try {
      const newItems = await this.#renderNextPage();

      if (newItems.length === 0) {
        if (loadGeneration === this.#cacheGeneration && loadMoreNext.href === fallbackUrl) {
          window.location.assign(fallbackUrl);
        }
        return;
      }

      if (isKeyboardActivation) {
        const firstInteractiveElement = newItems[0]?.querySelector(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
        );

        if (firstInteractiveElement instanceof HTMLElement) {
          firstInteractiveElement.focus();
        }
      }
    } catch (error) {
      console.error('Unable to load more collection products.', error);
      if (loadGeneration === this.#cacheGeneration && loadMoreNext.href === fallbackUrl) {
        window.location.assign(fallbackUrl);
      }
    } finally {
      if (loadMoreNext.isConnected) {
        loadMoreNext.removeAttribute('aria-busy');
        loadMoreNext.removeAttribute('aria-disabled');
      }
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      try {
        await this.#fetchSpecificPage(previousPage.page, previousPage.url);
      } catch (error) {
        console.error(`Unable to load collection page ${previousPage.page}.`, error);
        return;
      }

      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards, loadMoreNext } = this.refs;
    const isPrevious = type === 'previous';

    if (!isPrevious && loadMoreNext instanceof HTMLAnchorElement) {
      const href = loadMoreNext.getAttribute('href');
      const page = Number(loadMoreNext.dataset.page);

      if (href && Number.isInteger(page)) {
        return {
          page,
          url: new URL(href, window.location.href),
        };
      }
    }

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Copies Shopify's exact next-page URL from the rendered page into the live link.
   * @param {number} page
   */
  #syncNextPageControls(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const nextLink = parsedPage.querySelector('[ref="loadMoreNext"]');
    const { loadMoreNext, viewMoreNext } = this.refs;
    const nextHref = nextLink?.getAttribute('href');
    const nextPage = nextLink instanceof HTMLElement ? nextLink.dataset.page : undefined;

    // Lists without the OC progressive link keep their original infinite-scroll behavior.
    if (!(loadMoreNext instanceof HTMLAnchorElement)) return;

    if (nextHref && nextPage) {
      loadMoreNext.setAttribute('href', nextHref);
      loadMoreNext.dataset.page = nextPage;
      loadMoreNext.hidden = false;

      if (viewMoreNext instanceof HTMLElement) {
        viewMoreNext.hidden = false;
      }

      return;
    }

    loadMoreNext.hidden = true;
    loadMoreNext.removeAttribute('href');
    loadMoreNext.removeAttribute('data-page');

    if (viewMoreNext instanceof HTMLElement) {
      viewMoreNext.hidden = true;
      this.infinityScrollObserver?.unobserve(viewMoreNext);
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#cacheGeneration += 1;
    this.pages.clear();
    this.#activeRestoreId = '';

    const currentHistoryState =
      history.state && typeof history.state === 'object' ? history.state : undefined;
    if (currentHistoryState?.ocCollectionRestore) {
      const snapshotKey = String(currentHistoryState.ocCollectionRestore.snapshotKey || '');

      if (snapshotKey) {
        try {
          sessionStorage.removeItem(snapshotKey);
          if (sessionStorage.getItem('ocCollectionSnapshotKey') === snapshotKey) {
            sessionStorage.removeItem('ocCollectionSnapshotKey');
          }
        } catch {
          // Ignore storage cleanup failures.
        }
      }

      const nextHistoryState = { ...currentHistoryState };
      delete nextHistoryState.ocCollectionRestore;
      history.replaceState(nextHistoryState, '', window.location.href);
      history.scrollRestoration = 'auto';
    }

    // We need to wait for the DOM to be updated with the new filtered content
    // A filter can change the products without changing the total page count,
    // so react to the first section mutation instead of only data-last-page.
    let refreshed = false;
    const refreshPagination = () => {
      if (refreshed) return;
      refreshed = true;
      observer.disconnect();

      if (!this.isConnected) return;

      this.#observeViewMore();
      this.#fetchPage('next');
      this.#fetchPage('previous');
    };

    const observer = new MutationObserver(() => {
      requestAnimationFrame(refreshPagination);
    });

    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      if (grid.parentElement) {
        observer.observe(grid.parentElement, {
          childList: true,
        });
      }
    } else {
      observer.observe(this, {
        childList: true,
        subtree: true,
      });
    }

    // Fallback for a failed or no-op morph.
    setTimeout(refreshPagination, 3000);
  };
}